<template>
  <div class="tracker-panel ladder-tracker">
    <div class="ladder-header">
      <div class="side-title side-title--left">
        LIVRO COMPRADO · {{ spotUnits.length }} units
      </div>
      <div class="center-title">
        <span class="center-title-label">Preço Atual</span>
        <span class="center-title-value">{{ formatPrice(currentPrice) }}</span>
      </div>
      <div class="side-title side-title--right">
        {{ shortUnits.length }} units · LIVRO VENDIDO
      </div>
    </div>

    <div class="params-panel">
      <div class="params-title">Reservation Params</div>
      <div class="params-grid">
        <label class="param-item">
          <span>Sensibilidade Compra</span>
          <input v-model.number="sensitivityBuy" type="number" step="0.001" min="0" />
        </label>
        <label class="param-item">
          <span>Sensibilidade Venda</span>
          <input v-model.number="sensitivitySell" type="number" step="0.001" min="0" />
        </label>
        <label class="param-item">
          <span>Alocação Alvo Compra</span>
          <input v-model.number="targetAllocationBuy" type="number" step="1000" min="1" />
        </label>
        <label class="param-item">
          <span>Alocação Alvo Venda</span>
          <input v-model.number="targetAllocationSell" type="number" step="1000" min="1" />
        </label>
        <label class="param-item">
          <span>Volatilidade Diária (USD)</span>
          <input v-model.number="dailyVolatilityUsd" type="number" step="100" min="0" />
        </label>
      </div>
    </div>

    <div v-if="hasUnits" class="weight-scale">
      <div class="weight-scale-side">
        <span class="weight-scale-title">Weight (Livro Comprado)</span>
        <span class="weight-scale-subtitle">avgPrice: {{ formatPrice(spotOpenAvgPrice) }}</span>
        <span class="weight-scale-subtitle">Total: {{ formatWeight(spotTotalWeight) }} | Max unit: {{ formatWeight(spotMaxWeight) }}</span>
        <span class="weight-scale-subtitle">Reserva: {{ formatPrice(reservationBuyPrice) }} | Sat: {{ formatRatio(buySaturation) }}</span>
        <div class="weight-ruler weight-ruler--left">
          <span>0%</span>
          <span>{{ formatWeight(weightScaleMidSpot) }}</span>
          <span>{{ formatWeight(spotMaxWeight) }}</span>
        </div>
      </div>
      <div class="weight-scale-side weight-scale-side--right">
        <span class="weight-scale-title">Weight (Livro Vendido)</span>
        <span class="weight-scale-subtitle">avgPrice: {{ formatPrice(shortOpenAvgPrice) }}</span>
        <span class="weight-scale-subtitle">Total: {{ formatWeight(shortTotalWeight) }} | Max unit: {{ formatWeight(shortMaxWeight) }}</span>
        <span class="weight-scale-subtitle">Reserva: {{ formatPrice(reservationSellPrice) }} | Sat: {{ formatRatio(sellSaturation) }}</span>
        <div class="weight-ruler">
          <span>0%</span>
          <span>{{ formatWeight(weightScaleMidShort) }}</span>
          <span>{{ formatWeight(shortMaxWeight) }}</span>
        </div>
      </div>
    </div>

    <div v-if="hasUnits" ref="ladderStageScroll" class="ladder-stage-scroll">
      <div
        ref="ladderStageEl"
        class="ladder-stage"
        :style="{ height: `${ladderCanvasHeight}px` }"
        @pointerdown="onStagePointerDown"
        @pointermove="onStagePointerMove"
        @pointerup="onStagePointerUp"
        @pointercancel="onStagePointerCancel"
      >
        <div class="price-scale-left">
          <div
            v-for="tick in fixedPriceTicks"
            :key="`label-${tick}`"
            class="price-scale-item"
            :style="{ top: `${priceToY(tick)}%` }"
          >
            {{ formatPriceTick(tick) }}
          </div>
        </div>

        <div class="ladder-axis"></div>

        <div
          v-if="currentPrice > 0"
          class="current-price-line"
          :style="{ top: `${priceToY(currentPrice)}%` }"
        >
          <span class="current-price-label">
            C: {{ formatPrice(currentPrice) }}
          </span>
        </div>

        <div
          v-if="reservationBuyPrice > 0"
          class="reservation-line reservation-line--buy"
          :style="{ top: `${priceToY(reservationBuyPrice)}%` }"
        >
          <span class="reservation-label reservation-label--buy">
            R compra: {{ formatPrice(reservationBuyPrice) }}
          </span>
        </div>

        <div
          v-if="reservationSellPrice > 0"
          class="reservation-line reservation-line--sell"
          :style="{ top: `${priceToY(reservationSellPrice)}%` }"
        >
          <span class="reservation-label reservation-label--sell">
            R venda: {{ formatPrice(reservationSellPrice) }}
          </span>
        </div>

        <div
          v-for="tick in fixedPriceTicks"
          :key="`line-${tick}`"
          class="price-grid-line"
          :style="{ top: `${priceToY(tick)}%` }"
        ></div>

        <div
          v-for="unit in spotUnits"
          :key="unit.id"
          class="bar-row bar-row--left"
          :style="rowStyle(unit)"
        >
          <div
            class="unit-bar unit-bar--left"
            :class="{ 'unit-bar--selected': isUnitSelected(unit) }"
            :style="barWidthStyle(unit)"
            :data-unit-id="unit.id"
            :data-side="unit.side"
            @click.stop="selectUnit(unit, $event)"
          >
            <span class="bar-text">
              {{ formatPrice(unit.entryPrice) }} | {{ formatQty(unit.qty) }} | {{ formatPct(unit.distancePct) }} | {{ formatUsd(unit.pnl) }}
            </span>
          </div>
        </div>

        <div
          v-for="unit in shortUnits"
          :key="unit.id"
          class="bar-row bar-row--right"
          :style="rowStyle(unit)"
        >
          <div
            class="unit-bar unit-bar--right"
            :class="{ 'unit-bar--selected': isUnitSelected(unit) }"
            :style="barWidthStyle(unit)"
            :data-unit-id="unit.id"
            :data-side="unit.side"
            @click.stop="selectUnit(unit, $event)"
          >
            <span class="bar-text">
              {{ formatPrice(unit.entryPrice) }} | {{ formatQty(unit.qty) }} | {{ formatPct(unit.distancePct) }} | {{ formatUsd(unit.pnl) }}
            </span>
          </div>
        </div>

        <div
          v-if="dragSelection.active && dragSelection.hasMoved"
          class="selection-box"
          :style="selectionBoxStyle"
        ></div>
      </div>
    </div>

    <div v-if="hasUnits" class="unit-details-box">
      <div v-if="selectedUnits.length" class="details-content">
        <div class="details-heading">
          Selecao {{ selectedSide === 'spot' ? 'Manual (Spot)' : 'Short' }} · {{ selectedSummary.count }} units
        </div>
        <div class="details-grid">
          <div><span class="details-label">Lado:</span> {{ selectedSide }}</div>
          <div><span class="details-label">Qty total:</span> {{ formatQty(selectedSummary.qtyTotal) }}</div>
          <div><span class="details-label">Weight total:</span> {{ formatWeight(selectedSummary.weightTotal) }}</div>
          <div><span class="details-label">Weight ponderado:</span> {{ formatWeight(selectedSummary.weightWeighted) }}</div>
          <div><span class="details-label">Entry ponderado:</span> {{ formatPrice(selectedSummary.entryPriceWeighted) }}</div>
          <div><span class="details-label">DistancePct ponderada:</span> {{ formatPct(selectedSummary.distancePctWeighted) }}</div>
          <div><span class="details-label">Notional total:</span> {{ formatPrice(selectedSummary.notionalTotal) }}</div>
          <div>
            <span class="details-label">{{ selectedSummary.count === 1 ? 'P&L unit:' : 'P&L total:' }}</span>
            <span :class="selectedSummary.pnlTotal >= 0 ? 'profit-positive' : 'profit-negative'">
              {{ formatUsd(selectedSummary.pnlTotal) }}
            </span>
          </div>
          <div><span class="details-label">Preço Atual:</span> {{ formatPrice(currentPrice) }}</div>
        </div>
        <pre v-if="selectedUnits.length === 1" class="details-json">{{ prettyJson(selectedUnits[0].raw) }}</pre>
        <div v-else class="selected-list">
          {{ selectedUnits.map(unit => unit.displayId).join(', ') }}
        </div>
      </div>
      <div v-else class="empty-msg">
        Clique para selecionar. Shift/Cmd/Ctrl para multi-selecao no mesmo lado. Arraste caixa para selecionar por area.
      </div>
    </div>

    <div v-else class="empty-msg">
      Sem units em ledger/shortLedger para montar o ladder.
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

const CAPITAL_TOTAL = 96000;
const SENSITIVITY_BUY = 10;
const SENSITIVITY_SELL = 10;
const TARGET_ALLOCATION_BUY = 50000;
const TARGET_ALLOCATION_SELL = 30000;
const DAILY_VOLATILITY_USD = 3000;

const MIN_BAR_WIDTH_PCT = 8;
const MAX_BAR_WIDTH_PCT = 94;
const DUPLICATE_PRICE_OFFSET_PX = 8;
const FIXED_PRICE_MIN = 58000;
const FIXED_PRICE_MAX = 88000;
const FIXED_PRICE_STEP = 1000;
const PRICE_STEP_PX = 120;
const LADDER_TOP_BOTTOM_PADDING_PX = 120;
const MIN_LADDER_CANVAS_HEIGHT = 600;

export default {
  name: 'ladderTracker',
  data() {
    return {
      selectedUnitIds: [],
      selectedSide: null,
      hasAutoCentered: false,
      skipClickOnce: false,
      dragSelection: {
        active: false,
        hasMoved: false,
        additive: false,
        side: null,
        baseIds: [],
        startedOnUnitBar: false,
        startX: 0,
        startY: 0,
        currentX: 0,
        currentY: 0,
      },
      sensitivityBuy: SENSITIVITY_BUY,
      sensitivitySell: SENSITIVITY_SELL,
      targetAllocationBuy: TARGET_ALLOCATION_BUY,
      targetAllocationSell: TARGET_ALLOCATION_SELL,
      dailyVolatilityUsd: DAILY_VOLATILITY_USD,
    };
  },
  watch: {
    hasUnits() {
      this.tryAutoFocusCurrentPrice();
    },
    currentPrice() {
      this.tryAutoFocusCurrentPrice();
    },
    ladderCanvasHeight() {
      this.tryAutoFocusCurrentPrice();
    },
    spotUnits() {
      this.sanitizeSelection();
    },
    shortUnits() {
      this.sanitizeSelection();
    },
  },
  mounted() {
    this.tryAutoFocusCurrentPrice();
    window.addEventListener('keydown', this.handleGlobalKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleGlobalKeyDown);
  },
  computed: {
    ...mapState({
      ledger: state => state.ledger,
      shortLedger: state => state.shortLedger,
      marketData: state => state.marketData,
    }),

    currentPrice() {
      return Number(this.marketData?.btcUsd || 0);
    },

    spotUnitsRaw() {
      return Object.values(this.ledger || {})
        .filter(unit => unit.bucket === 'manual' && Number(unit.current?.qty || 0) > 0 && Number(unit.current?.avgPrice || 0) > 0)
        .map(unit => {
          const qty = Number(unit.current?.qty || 0);
          const entryPrice = Number(unit.current?.avgPrice || 0);
          return {
            id: `spot-${unit.unitID || entryPrice}`,
            displayId: unit.unitID || 'N/A',
            side: 'spot',
            entryPrice,
            qty,
            weight: this.calcWeight(qty, entryPrice),
            raw: unit,
          };
        });
    },

    shortUnitsRaw() {
      return Object.values(this.shortLedger || {})
        .filter(unit => unit.status === 'active' && Number(unit.remainingQty || 0) > 0 && Number(unit.origin?.sellPrice || 0) > 0)
        .map(unit => {
          const qty = Number(unit.remainingQty || 0);
          const entryPrice = Number(unit.origin?.sellPrice || 0);
          return {
            id: `short-${unit.shortUnitID || entryPrice}`,
            displayId: unit.shortUnitID || 'N/A',
            side: 'short',
            entryPrice,
            qty,
            weight: this.calcWeight(qty, entryPrice),
            raw: unit,
          };
        });
    },

    hasUnits() {
      return this.spotUnitsRaw.length > 0 || this.shortUnitsRaw.length > 0;
    },

    fixedPriceTicks() {
      const ticks = [];
      for (let price = FIXED_PRICE_MAX; price >= FIXED_PRICE_MIN; price -= FIXED_PRICE_STEP) {
        ticks.push(price);
      }
      return ticks;
    },

    scaleRange() {
      return {
        min: FIXED_PRICE_MIN,
        max: FIXED_PRICE_MAX,
      };
    },

    ladderCanvasHeight() {
      const steps = Math.max(this.fixedPriceTicks.length - 1, 1);
      return Math.max(
        steps * PRICE_STEP_PX + LADDER_TOP_BOTTOM_PADDING_PX,
        MIN_LADDER_CANVAS_HEIGHT
      );
    },

    spotMaxWeight() {
      return this.spotUnitsRaw.reduce((acc, unit) => Math.max(acc, unit.weight), 0);
    },

    shortMaxWeight() {
      return this.shortUnitsRaw.reduce((acc, unit) => Math.max(acc, unit.weight), 0);
    },

    spotTotalWeight() {
      return this.spotUnitsRaw.reduce((acc, unit) => acc + unit.weight, 0);
    },

    shortTotalWeight() {
      return this.shortUnitsRaw.reduce((acc, unit) => acc + unit.weight, 0);
    },

    spotOpenQty() {
      return this.spotUnitsRaw.reduce((acc, unit) => acc + unit.qty, 0);
    },

    shortOpenQty() {
      return this.shortUnitsRaw.reduce((acc, unit) => acc + unit.qty, 0);
    },

    spotOpenAllocation() {
      return this.spotUnitsRaw.reduce((acc, unit) => acc + (unit.qty * unit.entryPrice), 0);
    },

    shortOpenAllocation() {
      return this.shortUnitsRaw.reduce((acc, unit) => acc + (unit.qty * unit.entryPrice), 0);
    },

    spotOpenAvgPrice() {
      if (this.spotOpenQty <= 0) return 0;
      return this.spotOpenAllocation / this.spotOpenQty;
    },

    shortOpenAvgPrice() {
      if (this.shortOpenQty <= 0) return 0;
      return this.shortOpenAllocation / this.shortOpenQty;
    },

    targetAllocationBuyValue() {
      return Number(this.targetAllocationBuy || 0);
    },

    targetAllocationSellValue() {
      return Number(this.targetAllocationSell || 0);
    },

    sensitivityBuyValue() {
      return Number(this.sensitivityBuy || 0);
    },

    sensitivitySellValue() {
      return Number(this.sensitivitySell || 0);
    },

    dailyVolatilityUsdValue() {
      return Number(this.dailyVolatilityUsd || 0);
    },

    buySaturation() {
      if (this.targetAllocationBuyValue <= 0) return 0;
      return this.spotOpenAllocation / this.targetAllocationBuyValue;
    },

    sellSaturation() {
      if (this.targetAllocationSellValue <= 0) return 0;
      return this.shortOpenAllocation / this.targetAllocationSellValue;
    },

    reservationBuyPrice() {
      if (this.currentPrice <= 0) return 0;
      const adjustment = this.buySaturation * this.dailyVolatilityUsdValue; //this.sensitivityBuyValue * 
      return this.currentPrice - adjustment;
    },

    reservationSellPrice() {
      if (this.currentPrice <= 0) return 0;
      const adjustment = this.sellSaturation * this.dailyVolatilityUsdValue; //this.sensitivitySellValue * 
      return this.currentPrice + adjustment;
    },

    weightScaleMidSpot() {
      return this.spotMaxWeight / 2;
    },

    weightScaleMidShort() {
      return this.shortMaxWeight / 2;
    },

    spotUnits() {
      return this.normalizeSide(this.spotUnitsRaw, this.spotMaxWeight);
    },

    shortUnits() {
      return this.normalizeSide(this.shortUnitsRaw, this.shortMaxWeight);
    },

    allUnitsById() {
      return [...this.spotUnits, ...this.shortUnits].reduce((acc, unit) => {
        acc[unit.id] = unit;
        return acc;
      }, {});
    },

    selectedUnits() {
      return this.selectedUnitIds
        .map(id => this.allUnitsById[id])
        .filter(unit => unit && unit.side === this.selectedSide);
    },

    selectedSummary() {
      const units = this.selectedUnits;
      const qtyTotal = units.reduce((acc, unit) => acc + unit.qty, 0);
      const notionalTotal = units.reduce((acc, unit) => acc + unit.qty * unit.entryPrice, 0);
      const weightTotal = units.reduce((acc, unit) => acc + unit.weight, 0);
      const distanceWeightedBase = units.reduce((acc, unit) => acc + (unit.distancePct * unit.qty), 0);
      const weightWeightedBase = units.reduce((acc, unit) => acc + (unit.weight * unit.qty), 0);
      const pnlTotal = units.reduce((acc, unit) => acc + unit.pnl, 0);
      return {
        count: units.length,
        qtyTotal,
        notionalTotal,
        weightTotal,
        pnlTotal,
        weightWeighted: qtyTotal > 0 ? (weightWeightedBase / qtyTotal) : 0,
        entryPriceWeighted: qtyTotal > 0 ? (notionalTotal / qtyTotal) : 0,
        distancePctWeighted: qtyTotal > 0 ? (distanceWeightedBase / qtyTotal) : 0,
      };
    },

    selectionBoxStyle() {
      const minX = Math.min(this.dragSelection.startX, this.dragSelection.currentX);
      const maxX = Math.max(this.dragSelection.startX, this.dragSelection.currentX);
      const minY = Math.min(this.dragSelection.startY, this.dragSelection.currentY);
      const maxY = Math.max(this.dragSelection.startY, this.dragSelection.currentY);
      return {
        left: `${minX}px`,
        top: `${minY}px`,
        width: `${Math.max(maxX - minX, 0)}px`,
        height: `${Math.max(maxY - minY, 0)}px`,
      };
    },
  },

  methods: {
    calcWeight(qty, entryPrice) {
      return (Number(qty) * Number(entryPrice)) / CAPITAL_TOTAL;
    },

    calcDistancePct(entryPrice, side = 'spot') {
      if (!entryPrice || !this.currentPrice) return 0;
      if (side === 'short') {
        return ((entryPrice - this.currentPrice) / entryPrice) * 100;
      }
      return ((this.currentPrice - entryPrice) / entryPrice) * 100;
    },

    calcUnitPnl(entryPrice, qty, side = 'spot') {
      const safeEntry = Number(entryPrice || 0);
      const safeQty = Number(qty || 0);
      if (safeEntry <= 0 || safeQty <= 0 || !this.currentPrice) return 0;
      if (side === 'short') {
        return (safeEntry - this.currentPrice) * safeQty;
      }
      return (this.currentPrice - safeEntry) * safeQty;
    },

    isUnitSelected(unit) {
      return this.selectedSide === unit.side && this.selectedUnitIds.includes(unit.id);
    },

    sanitizeSelection() {
      if (!this.selectedUnitIds.length) {
        this.selectedSide = null;
        return;
      }
      const filtered = this.selectedUnitIds.filter(unitId => {
        const unit = this.allUnitsById[unitId];
        return unit && unit.side === this.selectedSide;
      });
      if (filtered.length !== this.selectedUnitIds.length) {
        this.selectedUnitIds = filtered;
      }
      if (!this.selectedUnitIds.length) {
        this.selectedSide = null;
      }
    },

    normalizeSide(units, maxWeightRef) {
      if (!units.length) return [];

      const groupedCounts = units.reduce((acc, unit) => {
        const key = this.priceKey(unit.entryPrice);
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      const groupedIndexes = {};

      return units
        .map(unit => {
          const key = this.priceKey(unit.entryPrice);
          const count = groupedCounts[key] || 1;
          const index = groupedIndexes[key] || 0;
          groupedIndexes[key] = index + 1;

          return {
            ...unit,
            distancePct: this.calcDistancePct(unit.entryPrice, unit.side),
            pnl: this.calcUnitPnl(unit.entryPrice, unit.qty, unit.side),
            yPct: this.priceToY(unit.entryPrice),
            offsetPx: (index - (count - 1) / 2) * DUPLICATE_PRICE_OFFSET_PX,
            widthPct: this.normalizeWidth(unit.weight, maxWeightRef),
          };
        })
        .sort((a, b) => b.entryPrice - a.entryPrice);
    },

    priceKey(price) {
      return Number(price || 0).toFixed(2);
    },

    normalizeWidth(weight, maxWeight) {
      if (maxWeight <= 0) return MIN_BAR_WIDTH_PCT;
      const ratio = Math.min(Math.max(weight / maxWeight, 0), 1);
      return MIN_BAR_WIDTH_PCT + ratio * (MAX_BAR_WIDTH_PCT - MIN_BAR_WIDTH_PCT);
    },

    priceToY(price) {
      const { min, max } = this.scaleRange;
      const span = max - min;
      if (span <= 0) return 50;
      const y = ((max - price) / span) * 100;
      return Math.min(Math.max(y, 0), 100);
    },

    rowStyle(unit) {
      return {
        top: `calc(${unit.yPct}% + ${unit.offsetPx}px)`,
      };
    },

    barWidthStyle(unit) {
      return {
        width: `${unit.widthPct}%`,
      };
    },

    formatPrice(value) {
      return Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
    },

    formatPriceTick(value) {
      const num = Number(value || 0);
      if (Math.abs(num) >= 1000) {
        return `${(num / 1000).toFixed(1)}k`;
      }
      return num.toFixed(2);
    },

    formatQty(value) {
      return Number(value || 0).toFixed(6);
    },

    formatWeight(value) {
      return `${(Number(value || 0) * 100).toFixed(2)}%`;
    },

    formatRatio(value) {
      return `${Number(value || 0).toFixed(2)}x`;
    },

    formatUsd(value) {
      const num = Number(value || 0);
      const abs = Math.abs(num).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `${num < 0 ? '-' : ''}$${abs}`;
    },

    formatPct(value) {
      const num = Number(value || 0);
      const signal = num > 0 ? '+' : '';
      return `${signal}${num.toFixed(2)}%`;
    },

    selectUnit(unit, event) {
      if (this.skipClickOnce) {
        this.skipClickOnce = false;
        return;
      }

      const additive = Boolean(event?.shiftKey || event?.metaKey || event?.ctrlKey);
      if (!additive) {
        this.selectedSide = unit.side;
        this.selectedUnitIds = [unit.id];
        return;
      }

      if (this.selectedSide !== unit.side) {
        this.selectedSide = unit.side;
        this.selectedUnitIds = [unit.id];
        return;
      }

      const alreadySelected = this.selectedUnitIds.includes(unit.id);
      this.selectedUnitIds = alreadySelected
        ? this.selectedUnitIds.filter(id => id !== unit.id)
        : [...this.selectedUnitIds, unit.id];

      if (!this.selectedUnitIds.length) {
        this.selectedSide = null;
      }
    },

    clearSelection() {
      this.selectedUnitIds = [];
      this.selectedSide = null;
    },

    prettyJson(value) {
      return JSON.stringify(value || {}, null, 2);
    },

    handleGlobalKeyDown(event) {
      if (event.key !== 'Escape') return;
      if (!this.selectedUnitIds.length) return;
      this.clearSelection();
      event.preventDefault();
    },

    onStagePointerDown(event) {
      if (event.button !== 0) return;
      const stage = this.$refs.ladderStageEl;
      const scrollContainer = this.$refs.ladderStageScroll;
      if (!stage || !scrollContainer) return;

      const pointer = this.getStagePointerCoords(event, stage, scrollContainer);
      const side = pointer.x < stage.clientWidth / 2 ? 'spot' : 'short';
      const additive = Boolean(event.shiftKey || event.metaKey || event.ctrlKey);
      const baseIds = additive && this.selectedSide === side ? [...this.selectedUnitIds] : [];

      this.dragSelection = {
        active: true,
        hasMoved: false,
        additive,
        side,
        baseIds,
        startedOnUnitBar: Boolean(event.target?.closest?.('.unit-bar')),
        startX: pointer.x,
        startY: pointer.y,
        currentX: pointer.x,
        currentY: pointer.y,
      };

    },

    onStagePointerMove(event) {
      if (!this.dragSelection.active) return;
      const stage = this.$refs.ladderStageEl;
      const scrollContainer = this.$refs.ladderStageScroll;
      if (!stage || !scrollContainer) return;

      const pointer = this.getStagePointerCoords(event, stage, scrollContainer);
      this.dragSelection.currentX = pointer.x;
      this.dragSelection.currentY = pointer.y;

      const dx = this.dragSelection.currentX - this.dragSelection.startX;
      const dy = this.dragSelection.currentY - this.dragSelection.startY;
      const moved = (dx * dx + dy * dy) > 16;
      if (!moved) return;

      this.dragSelection.hasMoved = true;
      this.applyDragSelection();
    },

    onStagePointerUp(event) {
      if (!this.dragSelection.active) return;

      if (this.dragSelection.hasMoved) {
        this.applyDragSelection();
        this.skipClickOnce = true;
        setTimeout(() => {
          this.skipClickOnce = false;
        }, 0);
      } else if (!this.dragSelection.startedOnUnitBar) {
        this.clearSelection();
      }

      this.resetDragSelection();
    },

    onStagePointerCancel() {
      this.resetDragSelection();
    },

    resetDragSelection() {
      this.dragSelection.active = false;
      this.dragSelection.hasMoved = false;
      this.dragSelection.baseIds = [];
      this.dragSelection.startedOnUnitBar = false;
    },

    getStagePointerCoords(event, stage, scrollContainer) {
      const stageRect = stage.getBoundingClientRect();
      const scrollRect = scrollContainer.getBoundingClientRect();
      const x = Math.min(Math.max(event.clientX - stageRect.left, 0), stage.clientWidth);
      const yInView = event.clientY - scrollRect.top;
      const y = Math.min(
        Math.max(scrollContainer.scrollTop + yInView, 0),
        stage.clientHeight
      );
      return { x, y };
    },

    getDragRect() {
      return {
        left: Math.min(this.dragSelection.startX, this.dragSelection.currentX),
        right: Math.max(this.dragSelection.startX, this.dragSelection.currentX),
        top: Math.min(this.dragSelection.startY, this.dragSelection.currentY),
        bottom: Math.max(this.dragSelection.startY, this.dragSelection.currentY),
      };
    },

    applyDragSelection() {
      const side = this.dragSelection.side;
      const idsInRect = this.findUnitsInRect(side);
      const merged = this.dragSelection.additive
        ? Array.from(new Set([...this.dragSelection.baseIds, ...idsInRect]))
        : idsInRect;

      this.selectedSide = merged.length ? side : null;
      this.selectedUnitIds = merged;
    },

    findUnitsInRect(side) {
      const stage = this.$refs.ladderStageEl;
      const scrollContainer = this.$refs.ladderStageScroll;
      if (!stage || !scrollContainer) return [];

      const selector = `.unit-bar[data-side="${side}"]`;
      const unitBars = stage.querySelectorAll(selector);
      const stageRect = stage.getBoundingClientRect();
      const rect = this.getDragRect();
      const hitIds = [];

      unitBars.forEach(el => {
        const unitId = el.getAttribute('data-unit-id');
        if (!unitId) return;

        const elRect = el.getBoundingClientRect();
        const left = elRect.left - stageRect.left;
        const right = elRect.right - stageRect.left;
        const top = elRect.top - stageRect.top;
        const bottom = elRect.bottom - stageRect.top;

        const intersects =
          right >= rect.left &&
          left <= rect.right &&
          bottom >= rect.top &&
          top <= rect.bottom;

        if (intersects) hitIds.push(unitId);
      });

      return hitIds;
    },

    tryAutoFocusCurrentPrice() {
      if (this.hasAutoCentered || !this.hasUnits || this.currentPrice <= 0) return;

      this.$nextTick(() => {
        const scrollContainer = this.$refs.ladderStageScroll;
        if (!scrollContainer) return;

        const targetPercent = this.priceToY(this.currentPrice) / 100;
        const scrollHeight = Number(scrollContainer.scrollHeight || 0);
        const viewportHeight = Number(scrollContainer.clientHeight || 0);
        const maxScrollTop = Math.max(scrollHeight - viewportHeight, 0);

        if (maxScrollTop <= 0) {
          this.hasAutoCentered = true;
          return;
        }

        const targetY = targetPercent * scrollHeight;
        const desiredTop = targetY - viewportHeight / 2;
        scrollContainer.scrollTop = Math.min(Math.max(desiredTop, 0), maxScrollTop);
        this.hasAutoCentered = true;
      });
    },
  },
};
</script>

<style scoped>
.ladder-tracker {
  gap: 14px;
}

.params-panel {
  border: 1px solid #2f3a52;
  border-radius: 10px;
  padding: 10px;
  background: #0f1524;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.params-title {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.78;
}

.params-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 8px;
}

.param-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 10px;
  color: #8ea2ca;
}

.param-item input {
  width: 100%;
  font-size: 11px;
  padding: 5px 8px;
  border: 1px solid #30405f;
  border-radius: 6px;
  background: #0a111d;
  color: #d6e2ff;
}

.param-readonly {
  justify-content: center;
  border: 1px solid #30405f;
  border-radius: 6px;
  padding: 5px 8px;
  background: #0a111d;
}

.param-readonly strong {
  font-size: 12px;
  color: #d6e2ff;
}

.weight-scale {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.weight-scale-side {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.weight-scale-title {
  font-size: 10px;
  opacity: 0.7;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.weight-scale-subtitle {
  font-size: 10px;
  color: #8ea2ca;
  opacity: 0.9;
}

.weight-scale-side--right {
  align-items: flex-end;
}

.weight-ruler {
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 8px;
  color: #8ea2ca;
  font-size: 10px;
}

.weight-ruler::before {
  content: '';
  grid-column: 1 / 4;
  height: 1px;
  background: #3a4a67;
  order: 1;
}

.weight-ruler span {
  order: 2;
  background: #111827;
  padding: 0 2px;
}

.weight-ruler--left span:first-child {
  justify-self: start;
}

.ladder-stage-scroll {
  max-height: 62vh;
  overflow-y: auto;
  border: 1px solid #2a3242;
  border-radius: 12px;
}

.ladder-header {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
}

.side-title {
  font-size: 11px;
  letter-spacing: 0.06em;
  opacity: 0.75;
  text-transform: uppercase;
}

.side-title--left {
  color: #43d8a1;
}

.side-title--right {
  text-align: right;
  color: #9f87ff;
}

.center-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.center-title-label {
  font-size: 10px;
  opacity: 0.75;
  text-transform: uppercase;
}

.center-title-value {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.03em;
}

.ladder-stage {
  position: relative;
  background: linear-gradient(180deg, #111827 0%, #0f1524 100%);
  overflow: hidden;
}

.price-scale-left {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 66px;
  border-right: 1px solid #2f3a52;
  background: rgba(15, 21, 36, 0.86);
  z-index: 5;
}

.price-scale-item {
  position: absolute;
  right: 8px;
  transform: translateY(-50%);
  font-size: 10px;
  color: #8fa1c0;
}

.ladder-axis {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 1px;
  background: #6d7b99;
  opacity: 0.7;
}

.price-grid-line {
  position: absolute;
  left: 66px;
  right: 0;
  border-top: 1px solid rgba(62, 76, 107, 0.35);
  z-index: 1;
}

.current-price-line {
  position: absolute;
  left: 66px;
  right: 0;
  border-top: 1px dashed #69a4ff;
  z-index: 4;
}

.current-price-label {
  position: absolute;
  right: 12px;
  top: -10px;
  font-size: 11px;
  color: #8ab7ff;
  background: #0d1422;
  padding: 1px 6px;
  border-radius: 4px;
}

.reservation-line {
  position: absolute;
  border-top: 1px dashed;
  z-index: 4;
}

.reservation-line--buy {
  left: 66px;
  right: 50%;
  border-top-color: #3bd19f;
}

.reservation-line--sell {
  left: 50%;
  right: 0;
  border-top-color: #b18bff;
}

.reservation-label {
  position: absolute;
  top: -10px;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 4px;
}

.reservation-label--buy {
  left: 8px;
  color: #56e1b0;
  background: #0d2c24;
}

.reservation-label--sell {
  right: 8px;
  color: #d0b6ff;
  background: #211735;
}

.selection-box {
  position: absolute;
  border: 1px dashed #7bb8ff;
  background: rgba(123, 184, 255, 0.14);
  z-index: 6;
  pointer-events: none;
}

.bar-row {
  position: absolute;
  height: 24px;
  z-index: 3;
  display: flex;
  align-items: center;
}

.bar-row--left {
  left: 66px;
  right: 50%;
  justify-content: flex-end;
  padding-right: 10px;
}

.bar-row--right {
  left: 50%;
  right: 0;
  justify-content: flex-start;
  padding-left: 10px;
}

.unit-bar {
  height: 100%;
  display: flex;
  align-items: center;
  border-radius: 6px;
  border: 1px solid;
  overflow: hidden;
  padding: 0 8px;
  cursor: pointer;
}

.unit-bar--selected {
  box-shadow: 0 0 0 2px #f5b942 inset;
}

.unit-bar--left {
  margin-left: auto;
  background: rgba(68, 227, 168, 0.25);
  border-color: rgba(68, 227, 168, 0.55);
}

.unit-bar--right {
  margin-right: auto;
  background: rgba(152, 120, 255, 0.26);
  border-color: rgba(152, 120, 255, 0.6);
}

.bar-text {
  width: 100%;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bar-row--left .bar-text {
  text-align: right;
}

.bar-row--right .bar-text {
  text-align: left;
}

.unit-details-box {
  border: 1px solid #2f3a52;
  border-radius: 10px;
  padding: 10px;
  background: #0f1524;
}

.details-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.details-heading {
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.03em;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px 12px;
  font-size: 11px;
}

.details-label {
  opacity: 0.72;
}

.details-json {
  max-height: 220px;
  overflow: auto;
  border: 1px solid #30405f;
  border-radius: 8px;
  background: #0a111d;
  padding: 8px;
  font-size: 10px;
  line-height: 1.35;
  white-space: pre-wrap;
}

.selected-list {
  font-size: 11px;
  color: #bfcbe4;
  border: 1px solid #30405f;
  border-radius: 8px;
  background: #0a111d;
  padding: 8px;
}
</style>
